/**
 * AdminLeadCategories — CRUD per categorie e ricette del flusso /preventivo (Step 4).
 * Supporta drag&drop (DnD-Kit), creazione, modifica, eliminazione, abilita/disabilita.
 */
import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from './AdminLayout';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useToast } from '../../hooks/use-toast';
import LoadingSpinner from '../LoadingSpinner';
import {
  Plus, Edit, Trash2, GripVertical, Eye, EyeOff, X, Save, AlertTriangle, ChefHat, Tag,
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, arrayMove, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = process.env.REACT_APP_BACKEND_URL;
const DB_CATEGORIES = ['liquori', 'amari', 'gin', 'grappe', 'vodka', 'whisky', 'rum'];

const slugify = (s) => (s || '')
  .toString()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9-]+/g, '-')
  .replace(/(^-+|-+$)/g, '');

// ------------------------------------------------------------
// MAIN COMPONENT
// ------------------------------------------------------------
const AdminLeadCategories = () => {
  const { adminToken } = useAdminAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState('categories'); // 'categories' | 'recipes'
  const [categories, setCategories] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [products, setProducts] = useState([]); // listino prodotti per dropdown "Prodotto collegato"
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {kind, data}
  const [creating, setCreating] = useState(null); // {kind, prefillCategoryId?}

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${adminToken}`,
  });

  const loadAll = async () => {
    try {
      setLoading(true);
      const [cRes, rRes, pRes] = await Promise.all([
        fetch(`${API}/api/lead-categories/admin`, { headers: authHeaders() }),
        fetch(`${API}/api/lead-recipes/admin`, { headers: authHeaders() }),
        fetch(`${API}/api/products/liquor-types`),
      ]);
      if (!cRes.ok || !rRes.ok) throw new Error('Errore caricamento');
      setCategories(await cRes.json());
      setRecipes(await rRes.json());
      if (pRes.ok) setProducts(await pRes.json());
    } catch (e) {
      toast({ title: 'Errore', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-line */ }, []);

  // ---------- DnD save handlers ----------
  const persistCategoryOrder = async (orderedIds) => {
    try {
      const res = await fetch(`${API}/api/lead-categories/admin/reorder`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ ordered_ids: orderedIds }),
      });
      if (!res.ok) throw new Error('Riordino fallito');
      toast({ title: 'Ordine aggiornato' });
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
      loadAll();
    }
  };

  const persistRecipeOrder = async (orderedIds) => {
    try {
      const res = await fetch(`${API}/api/lead-recipes/admin/reorder`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ ordered_ids: orderedIds }),
      });
      if (!res.ok) throw new Error('Riordino fallito');
      toast({ title: 'Ordine aggiornato' });
    } catch (e) {
      toast({ title: 'Errore', description: e.message, variant: 'destructive' });
      loadAll();
    }
  };

  // ---------- CRUD handlers ----------
  const toggleActive = async (kind, item) => {
    const url = kind === 'category'
      ? `${API}/api/lead-categories/admin/${item.id}`
      : `${API}/api/lead-recipes/admin/${item.id}`;
    const res = await fetch(url, {
      method: 'PUT', headers: authHeaders(),
      body: JSON.stringify({ is_active: !item.is_active }),
    });
    if (res.ok) {
      toast({ title: item.is_active ? 'Disabilitato' : 'Attivato' });
      loadAll();
    } else {
      toast({ title: 'Errore', variant: 'destructive' });
    }
  };

  const removeItem = async (kind, item) => {
    const label = kind === 'category' ? 'categoria' : 'ricetta';
    if (!window.confirm(`Eliminare la ${label} "${item.label || item.name}"? Operazione irreversibile.`)) return;
    const url = kind === 'category'
      ? `${API}/api/lead-categories/admin/${item.id}`
      : `${API}/api/lead-recipes/admin/${item.id}`;
    const res = await fetch(url, { method: 'DELETE', headers: authHeaders() });
    if (res.ok) {
      toast({ title: 'Eliminato' });
      loadAll();
    } else {
      const data = await res.json().catch(() => ({}));
      toast({ title: 'Errore', description: data.detail || 'Eliminazione fallita', variant: 'destructive' });
    }
  };

  // ---------- Render ----------
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Categorie & Ricette /preventivo</h1>
            <p className="mt-1 text-gray-600">
              Gestisci le card mostrate nello Step 4 del questionario lead (<code className="bg-gray-100 px-1 rounded text-xs">/preventivo</code>)
              e le sotto-ricette collaudate.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreating({ kind: tab === 'categories' ? 'category' : 'recipe' })}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
            data-testid="lead-cat-new-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {tab === 'categories' ? 'Nuova Categoria' : 'Nuova Ricetta'}
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 flex gap-6">
          <TabButton active={tab === 'categories'} onClick={() => setTab('categories')} testid="tab-categories">
            <Tag className="w-4 h-4 inline mr-1" /> Categorie ({categories.length})
          </TabButton>
          <TabButton active={tab === 'recipes'} onClick={() => setTab('recipes')} testid="tab-recipes">
            <ChefHat className="w-4 h-4 inline mr-1" /> Ricette ({recipes.length})
          </TabButton>
        </div>

        {loading ? (
          <div className="py-16 text-center"><LoadingSpinner /></div>
        ) : tab === 'categories' ? (
          <CategoriesTable
            categories={categories}
            onReorder={(ids) => { setCategories((prev) => {
              const m = new Map(prev.map((c) => [c.id, c]));
              return ids.map((id) => m.get(id)).filter(Boolean);
            }); persistCategoryOrder(ids); }}
            onEdit={(c) => setEditing({ kind: 'category', data: c })}
            onToggle={(c) => toggleActive('category', c)}
            onDelete={(c) => removeItem('category', c)}
            onAddRecipe={(c) => setCreating({ kind: 'recipe', prefillCategoryId: c.id })}
          />
        ) : (
          <RecipesTable
            recipes={recipes}
            categories={categories}
            onReorder={(ids) => { setRecipes((prev) => {
              const m = new Map(prev.map((r) => [r.id, r]));
              return ids.map((id) => m.get(id)).filter(Boolean);
            }); persistRecipeOrder(ids); }}
            onEdit={(r) => setEditing({ kind: 'recipe', data: r })}
            onToggle={(r) => toggleActive('recipe', r)}
            onDelete={(r) => removeItem('recipe', r)}
          />
        )}
      </div>

      {(editing || creating) && (
        <EditorModal
          kind={editing?.kind || creating?.kind}
          initial={editing?.data || null}
          prefillCategoryId={creating?.prefillCategoryId}
          categories={categories}
          products={products}
          onClose={() => { setEditing(null); setCreating(null); }}
          onSaved={() => { setEditing(null); setCreating(null); loadAll(); }}
          authHeaders={authHeaders}
          toast={toast}
        />
      )}
    </AdminLayout>
  );
};

// ------------------------------------------------------------
// TabButton
// ------------------------------------------------------------
const TabButton = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`px-1 pb-3 -mb-px text-sm font-semibold border-b-2 transition-colors ${
      active ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-700'
    }`}
  >{children}</button>
);

// ------------------------------------------------------------
// CategoriesTable (drag & drop)
// ------------------------------------------------------------
const CategoriesTable = ({ categories, onReorder, onEdit, onToggle, onDelete, onAddRecipe }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const ids = useMemo(() => categories.map((c) => c.id), [categories]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    const next = arrayMove(ids, oldIndex, newIndex);
    onReorder(next);
  };

  if (categories.length === 0) {
    return <EmptyState label="Nessuna categoria. Crea la prima." />;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="bg-white rounded-lg shadow-md divide-y divide-gray-100">
          <div className="grid grid-cols-[40px_60px_1fr_140px_120px_180px] items-center px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 rounded-t-lg">
            <span></span>
            <span>Icona</span>
            <span>Categoria</span>
            <span>Listino</span>
            <span>Stato</span>
            <span className="text-right pr-2">Azioni</span>
          </div>
          {categories.map((c) => (
            <SortableCategoryRow key={c.id} item={c}
              onEdit={() => onEdit(c)} onToggle={() => onToggle(c)} onDelete={() => onDelete(c)}
              onAddRecipe={() => onAddRecipe(c)} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

const SortableCategoryRow = ({ item, onEdit, onToggle, onDelete, onAddRecipe }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div
      ref={setNodeRef} style={style}
      data-testid={`lead-cat-row-${item.id}`}
      className={`grid grid-cols-[40px_60px_1fr_140px_120px_180px] items-center px-4 py-3 ${item.is_active ? '' : 'bg-gray-50'} hover:bg-purple-50/40`}
    >
      <button
        {...attributes} {...listeners}
        className="cursor-grab text-gray-400 hover:text-gray-600 flex items-center justify-center"
        data-testid={`lead-cat-drag-${item.id}`}
        aria-label="Trascina per riordinare"
      ><GripVertical className="w-4 h-4" /></button>
      <span className="text-2xl">{item.icon}</span>
      <div className="min-w-0">
        <div className="font-semibold text-gray-900 truncate">{item.label}</div>
        <div className="text-[11px] font-mono text-gray-500 truncate">id: {item.id} · {item.default_alcohol}% vol.</div>
        <div className="text-[11px] text-gray-500 mt-0.5 truncate">{item.description}</div>
      </div>
      <span className="text-xs font-mono text-gray-600">{item.db_category}</span>
      <div className="flex flex-wrap gap-1">
        {item.is_active ? (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">Attiva</span>
        ) : (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-600">Disattiva</span>
        )}
        {item.allow_custom && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-700">Custom</span>
        )}
        {item.has_recipes && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700">Ricette</span>
        )}
        {item.linked_product_id && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-700" title={`Collegato a: ${item.linked_product_id}`}>🔗 Collegato</span>
        )}
      </div>
      <div className="flex items-center justify-end gap-1.5 pr-1">
        <IconBtn onClick={onAddRecipe} title="Aggiungi ricetta" testid={`lead-cat-addrec-${item.id}`}><ChefHat className="w-4 h-4 text-amber-600" /></IconBtn>
        <IconBtn onClick={onToggle} title={item.is_active ? 'Disabilita' : 'Abilita'} testid={`lead-cat-toggle-${item.id}`}>
          {item.is_active ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-green-600" />}
        </IconBtn>
        <IconBtn onClick={onEdit} title="Modifica" testid={`lead-cat-edit-${item.id}`}><Edit className="w-4 h-4 text-blue-600" /></IconBtn>
        <IconBtn onClick={onDelete} title="Elimina" testid={`lead-cat-del-${item.id}`}><Trash2 className="w-4 h-4 text-red-600" /></IconBtn>
      </div>
    </div>
  );
};

// ------------------------------------------------------------
// RecipesTable
// ------------------------------------------------------------
const RecipesTable = ({ recipes, categories, onReorder, onEdit, onToggle, onDelete }) => {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));
  const ids = useMemo(() => recipes.map((r) => r.id), [recipes]);
  const catMap = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    const next = arrayMove(ids, oldIndex, newIndex);
    onReorder(next);
  };

  if (recipes.length === 0) {
    return <EmptyState label="Nessuna ricetta. Crea la prima ricetta collaudata." />;
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="bg-white rounded-lg shadow-md divide-y divide-gray-100">
          <div className="grid grid-cols-[40px_60px_1fr_160px_100px_140px] items-center px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 rounded-t-lg">
            <span></span>
            <span>Icona</span>
            <span>Ricetta</span>
            <span>Categoria</span>
            <span>Stato</span>
            <span className="text-right pr-2">Azioni</span>
          </div>
          {recipes.map((r) => (
            <SortableRecipeRow key={r.id} item={r} catMap={catMap}
              onEdit={() => onEdit(r)} onToggle={() => onToggle(r)} onDelete={() => onDelete(r)} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
};

const SortableRecipeRow = ({ item, catMap, onEdit, onToggle, onDelete }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const parent = catMap.get(item.category_id);
  return (
    <div
      ref={setNodeRef} style={style}
      data-testid={`lead-rec-row-${item.id}`}
      className={`grid grid-cols-[40px_60px_1fr_160px_100px_140px] items-center px-4 py-3 ${item.is_active ? '' : 'bg-gray-50'} hover:bg-purple-50/40`}
    >
      <button
        {...attributes} {...listeners}
        className="cursor-grab text-gray-400 hover:text-gray-600"
        data-testid={`lead-rec-drag-${item.id}`}
        aria-label="Trascina per riordinare"
      ><GripVertical className="w-4 h-4" /></button>
      <span className="text-2xl">{item.icon}</span>
      <div className="min-w-0">
        <div className="font-semibold text-gray-900 truncate">{item.name}</div>
        <div className="text-[11px] font-mono text-gray-500 truncate">id: {item.id}</div>
        <div className="text-[11px] text-gray-500 mt-0.5 truncate">{item.description}</div>
        {item.tags && item.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {item.tags.map((t) => (
              <span key={t} className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-700">{t}</span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5">
        {parent ? (
          <>
            <span className="text-xl">{parent.icon}</span>
            <span className="text-sm text-gray-700 truncate">{parent.label}</span>
          </>
        ) : (
          <span className="text-xs text-red-500 inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> orfana</span>
        )}
      </div>
      <div>
        {item.is_active ? (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-100 text-green-700">Attiva</span>
        ) : (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-200 text-gray-600">Disattiva</span>
        )}
      </div>
      <div className="flex items-center justify-end gap-1.5 pr-1">
        <IconBtn onClick={onToggle} title={item.is_active ? 'Disabilita' : 'Abilita'} testid={`lead-rec-toggle-${item.id}`}>
          {item.is_active ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-green-600" />}
        </IconBtn>
        <IconBtn onClick={onEdit} title="Modifica" testid={`lead-rec-edit-${item.id}`}><Edit className="w-4 h-4 text-blue-600" /></IconBtn>
        <IconBtn onClick={onDelete} title="Elimina" testid={`lead-rec-del-${item.id}`}><Trash2 className="w-4 h-4 text-red-600" /></IconBtn>
      </div>
    </div>
  );
};

// ------------------------------------------------------------
// EditorModal — Create/Edit category or recipe
// ------------------------------------------------------------
const EditorModal = ({ kind, initial, prefillCategoryId, categories, products = [], onClose, onSaved, authHeaders, toast }) => {
  const isEdit = !!initial;
  const isCategory = kind === 'category';
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => isCategory
    ? {
      id: initial?.id || '',
      label: initial?.label || '',
      icon: initial?.icon || '🍶',
      default_alcohol: initial?.default_alcohol ?? 25,
      db_category: initial?.db_category || 'liquori',
      description: initial?.description || '',
      micro_copy: initial?.micro_copy || '',
      allow_custom: initial?.allow_custom ?? true,
      has_recipes: initial?.has_recipes ?? false,
      is_active: initial?.is_active ?? true,
      linked_product_id: initial?.linked_product_id || '',
    }
    : {
      id: initial?.id || '',
      category_id: initial?.category_id || prefillCategoryId || (categories[0]?.id || ''),
      name: initial?.name || '',
      icon: initial?.icon || '🍶',
      tags: initial?.tags?.join(', ') || '',
      description: initial?.description || '',
      tasting_notes: initial?.tasting_notes || '',
      is_active: initial?.is_active ?? true,
    });

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Auto-slug while creating
  useEffect(() => {
    if (isEdit) return;
    if (isCategory && !form.id && form.label) setField('id', slugify(form.label));
    if (!isCategory && !form.id && form.name) setField('id', slugify(form.name));
    // eslint-disable-next-line
  }, [form.label, form.name]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const baseUrl = isCategory ? '/api/lead-categories/admin' : '/api/lead-recipes/admin';
      const url = isEdit ? `${API}${baseUrl}/${initial.id}` : `${API}${baseUrl}`;
      const method = isEdit ? 'PUT' : 'POST';

      const payload = isCategory ? {
        ...(isEdit ? {} : { id: form.id.trim() }),
        label: form.label.trim(),
        icon: form.icon.trim() || '🍶',
        default_alcohol: parseFloat(form.default_alcohol),
        db_category: form.db_category,
        description: form.description || '',
        micro_copy: form.micro_copy || '',
        allow_custom: !!form.allow_custom,
        has_recipes: !!form.has_recipes,
        is_active: !!form.is_active,
        // In edit invio sempre il campo (anche '' per scollegare). In create solo se valorizzato.
        ...(isEdit ? { linked_product_id: form.linked_product_id || '' }
                   : (form.linked_product_id ? { linked_product_id: form.linked_product_id } : {})),
      } : {
        ...(isEdit ? {} : { id: form.id.trim() }),
        category_id: form.category_id,
        name: form.name.trim(),
        icon: form.icon.trim() || '🍶',
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        description: form.description || '',
        tasting_notes: form.tasting_notes || '',
        is_active: !!form.is_active,
      };

      // Validation: id always required on create
      if (!isEdit && !payload.id) {
        toast({ title: 'ID mancante', description: 'Inserisci un ID slug univoco (es. amaro)', variant: 'destructive' });
        setSaving(false);
        return;
      }
      if (isCategory && !payload.label) {
        toast({ title: 'Nome mancante', variant: 'destructive' });
        setSaving(false);
        return;
      }
      if (!isCategory && (!payload.name || !payload.category_id)) {
        toast({ title: 'Compila nome e categoria', variant: 'destructive' });
        setSaving(false);
        return;
      }

      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(payload) });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${res.status}`);
      }
      toast({ title: isEdit ? 'Aggiornato' : 'Creato' });
      onSaved();
    } catch (e) {
      toast({ title: 'Errore', description: String(e.message || e), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid={`lead-${isCategory ? 'cat' : 'rec'}-modal`}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-gray-900">
            {isEdit ? 'Modifica' : 'Nuova'} {isCategory ? 'categoria' : 'ricetta'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid="lead-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* ID (creation only) */}
          {!isEdit && (
            <Field label="ID slug *" hint="Lowercase, solo a-z, 0-9, trattini. Deve corrispondere all'id del listino prodotti per ereditare il prezzo.">
              <input
                type="text"
                value={form.id}
                onChange={(e) => setField('id', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm"
                placeholder={isCategory ? 'es. amaro' : 'es. amaro-agrumato'}
                data-testid="lead-modal-input-id"
              />
            </Field>
          )}

          {/* Icon + label/name */}
          <div className="grid grid-cols-[80px_1fr] gap-3">
            <Field label="Icona">
              <input type="text" value={form.icon} onChange={(e) => setField('icon', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-2xl text-center"
                maxLength={4} data-testid="lead-modal-input-icon" />
            </Field>
            <Field label={isCategory ? 'Nome categoria *' : 'Nome ricetta *'}>
              <input type="text"
                value={isCategory ? form.label : form.name}
                onChange={(e) => setField(isCategory ? 'label' : 'name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                data-testid="lead-modal-input-name" />
            </Field>
          </div>

          {isCategory ? (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Gradazione default (% vol.)">
                  <input type="number" min={0} max={100} step={0.5}
                    value={form.default_alcohol}
                    onChange={(e) => setField('default_alcohol', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    data-testid="lead-modal-input-alcohol" />
                </Field>
                <Field label="Categoria listino DB" hint="Usata per filtri / coerenza con /admin/products">
                  <select value={form.db_category} onChange={(e) => setField('db_category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    data-testid="lead-modal-input-dbcat">
                    {DB_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Descrizione (sotto al nome nella card)">
                <textarea value={form.description} onChange={(e) => setField('description', e.target.value)}
                  rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="lead-modal-input-desc" />
              </Field>
              <Field label="Micro-copy dopo selezione (es. 'Perfetto — abbiamo 5 ricette per te.')">
                <input type="text" value={form.micro_copy} onChange={(e) => setField('micro_copy', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="lead-modal-input-micro" />
              </Field>

              {/* Collegamento esplicito a un prodotto del listino */}
              <Field
                label="🔗 Prodotto del listino collegato (opzionale)"
                hint="Se valorizzato, il prezzo di questa categoria nel /preventivo verrà preso dal prodotto qui selezionato. Utile per evitare duplicati quando hai già il prodotto in /admin/products con un id diverso dallo slug della categoria."
              >
                <select
                  value={form.linked_product_id || ''}
                  onChange={(e) => setField('linked_product_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="lead-modal-input-linked-product"
                >
                  <option value="">— Auto-match per ID slug (default) —</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.id === form.id ? '★ ' : ''}{p.name} · €{Number(p.basePrice || 0).toFixed(2)} · id: {p.id}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="flex flex-wrap gap-4">
                <Toggle checked={form.allow_custom} onChange={(v) => setField('allow_custom', v)} testid="lead-modal-toggle-custom">
                  Consenti ricetta personalizzata
                </Toggle>
                <Toggle checked={form.is_active} onChange={(v) => setField('is_active', v)} testid="lead-modal-toggle-active">
                  Attiva (visibile in /preventivo)
                </Toggle>
              </div>
            </>
          ) : (
            <>
              <Field label="Categoria padre *">
                <select value={form.category_id} onChange={(e) => setField('category_id', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  data-testid="lead-modal-input-parent">
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.label}</option>)}
                </select>
              </Field>
              <Field label="Tag (separati da virgola)" hint="Es: Agrumato, Fresco, Leggero">
                <input type="text" value={form.tags} onChange={(e) => setField('tags', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="lead-modal-input-tags" />
              </Field>
              <Field label="Descrizione breve">
                <textarea value={form.description} onChange={(e) => setField('description', e.target.value)}
                  rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="lead-modal-input-desc" />
              </Field>
              <Field label="Note di degustazione (mostrate cliccando 'Mostra note')">
                <textarea value={form.tasting_notes} onChange={(e) => setField('tasting_notes', e.target.value)}
                  rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  data-testid="lead-modal-input-tasting" />
              </Field>
              <Toggle checked={form.is_active} onChange={(v) => setField('is_active', v)} testid="lead-modal-toggle-active">
                Attiva (visibile in /preventivo)
              </Toggle>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 hover:text-gray-900">Annulla</button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 disabled:opacity-50"
            data-testid="lead-modal-save">
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Salvataggio…' : (isEdit ? 'Salva' : 'Crea')}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children, hint }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-700 mb-1">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
  </div>
);

const Toggle = ({ checked, onChange, children, testid }) => (
  <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)}
      className="rounded text-purple-600 focus:ring-purple-500" data-testid={testid} />
    {children}
  </label>
);

const IconBtn = ({ onClick, children, title, testid }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testid}
    className="p-1.5 rounded hover:bg-gray-100 transition-colors">
    {children}
  </button>
);

const EmptyState = ({ label }) => (
  <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">{label}</div>
);

export default AdminLeadCategories;
